import {
  Component,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import { Router, NavigationEnd, NavigationStart,  Event as RouterEvent } from '@angular/router';
import { BnNgIdleService } from 'bn-ng-idle';
import { DialogControlService } from './shared/services/dialog-control.service';
import { Meta } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';
import { catchError, filter, map, mergeMap, takeUntil } from 'rxjs/operators';
import { of, combineLatest, Subject } from 'rxjs';
import { CommonService } from '@core/services/common.service';
import { AuthService } from '@core/services/auth.service';
import { EncryptionStorageService } from '@core/services/encryption-storage.service';
import { ConfigService } from '@core/services/config.service';

export interface RouteData {
  title: string;
  slug: string;
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'truckLotsFE';
  previousUrl: string | null = null;
  currentUrl: string | null = null;
  private lastRoute: string | null = null;

  private readonly destroy$ = new Subject<void>();

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly bnIdle: BnNgIdleService,
    private readonly router: Router,
    private readonly dialogControlService: DialogControlService,
    private readonly meta: Meta,
    private readonly renderer: Renderer2,
    private readonly commonService: CommonService,
    private readonly authService: AuthService,
    private readonly encryptStorage: EncryptionStorageService,
    private readonly configService: ConfigService
  ) {}

  ngOnInit(): void {
    this.initPageLogging();
    this.initIdleWatcher();
    this.trackPreviousUrl();
    this.handleMetaTags();
    this.loadGoogleMaps();
  }
  
  /** ---------------- INIT HELPERS ---------------- */

  private initPageLogging(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.router.routerState.root),
        map((route) => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        mergeMap((route) =>
          combineLatest([route?.data ?? of({}), route?.params ?? of({})])
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(([data]) => {
        const typedData = data as RouteData;
        if (this.authService.isLoggedIn()) {
          this.commonService
            .getPageLogs({ message: typedData.title })
            .pipe(
              catchError(() => of(null)),
              takeUntil(this.destroy$)
            )
            .subscribe();
        }
      });
  }

  private initIdleWatcher(): void {
    const token = this.encryptStorage.getItem('token');
    if (!token) return;

    this.bnIdle
      .startWatching(36000) // 10 hours
      .pipe(takeUntil(this.destroy$))
      .subscribe((isIdle) => {
        if (isIdle) {
          this.encryptStorage.removeItem('token');
          this.router.navigate(['login']);
        }
      });
  }

  private trackPreviousUrl(): void {
    this.router.events
  .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
  .subscribe((event) => {
    this.previousUrl = this.currentUrl;
    this.currentUrl = event.urlAfterRedirects; // safer than `event.url`
    this.encryptStorage.setItem('previousUrl', this.previousUrl);
  });

  }

  private handleMetaTags(): void {
    const currentUrl = window.location.pathname;

    // Remove meta tag if on booking pages
    if (
      currentUrl.includes('transflow-booking') ||
      currentUrl.includes('transflow-create-booking')
    ) {
      this.removeMetaTags();
      return;
    }

    // Otherwise add meta tag
    const metaTag = this.renderer.createElement('meta');
    this.renderer.setAttribute(metaTag, 'name', 'apple-itunes-app');
    this.renderer.setAttribute(
      metaTag,
      'content',
      `app-id=${this.configService.get('appId')}, app-argument=${this.configService.get('appUrls').ios}`
    );
    this.renderer.appendChild(this.document.head, metaTag);
  }

  private loadGoogleMaps(): void {
    if (document.getElementById('googleMapsScript')) return;

    const script = document.createElement('script');
    script.id = 'googleMapsScript';
    script.src = `${this.configService.get('mapUrls').googleMapsBase}?key=${this.configService.get('googleMapsApiKey')}&libraries=places,geometry`;
    script.async = true;
    document.head.appendChild(script);
  }

  /** ---------------- HELPERS ---------------- */

  removeMetaTags(): void {
    this.meta.removeTag('name="apple-itunes-app"');
  }

  @HostListener('window:popstate')
  onPopState(): void {
    this.dialogControlService.closeDialog();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.removeMetaTags();
  }
}
